import { useCallback } from 'react';

import { MessagingPlatform, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import {
  useCommunicationIntentsStore,
  useInboxEntriesStore,
  useUserCommunicationsStore,
} from '../../../store';
import { hasLinkedDoBox } from '../../../utils/userCommunicationHelpers';
import { useUpdateInboxEntryCollections } from '../../InboxEntries';

type InboxEntryWithCommunications = {
  id: string;
  communicationIds?: string[];
  communications?: Partial<UserCommunication>[];
};

const findEntryCommunications = (
  inboxEntryId: string,
  messageIds: string[],
  inboxEntries: InboxEntryWithCommunications[],
): UserCommunication[] => {
  const entry = inboxEntries.find((e) => e.id === inboxEntryId);
  if (!entry?.communications?.length) {
    return [];
  }

  const messageIdSet = new Set(messageIds);
  return entry.communications.filter(
    (comm) => comm.messageId && messageIdSet.has(comm.messageId),
  ) as UserCommunication[];
};

const collectMessageIdsToUnlink = (
  communications: Partial<UserCommunication>[],
) => {
  const ids = new Set<string>();

  for (const comm of communications) {
    if (comm.messageId) {
      ids.add(comm.messageId);
    }
    comm.threadMessages?.forEach((t) => ids.add(t.messageId));
  }

  return ids;
};

export interface UnlinkOptions {
  onComplete?: () => void;
  onClearSelection?: () => void;
}

export interface UseUnlinkUserCommunicationsResult {
  handleUnlinkUserCommunications: (
    inboxEntryId: string,
    messageIds: string | string[],
    options?: UnlinkOptions,
  ) => Promise<void>;
}

export const useUnlinkUserCommunications =
  (): UseUnlinkUserCommunicationsResult => {
    const { handleUpdateInboxEntryCollections } =
      useUpdateInboxEntryCollections();
    const handleUnlinkUserCommunications = useCallback(
      async (
        inboxEntryId: string,
        messageIds: string | string[],
        options?: UnlinkOptions,
      ): Promise<void> => {
        const { modifyUserCommunication } =
          useUserCommunicationsStore.getState();
        const { clearIntents } = useCommunicationIntentsStore.getState();
        const { inboxEntries, unlinkUserCommunicationsFromInboxEntry } =
          useInboxEntriesStore.getState().taskManager.activeTaskGroup;
        const ids = Array.isArray(messageIds) ? messageIds : [messageIds];
        if (!ids.length) {
          return;
        }

        try {
          const comms = findEntryCommunications(
            inboxEntryId,
            ids,
            inboxEntries,
          );

          if (comms.length === 0) {
            return;
          }

          const messageIdsToUnlink = collectMessageIdsToUnlink(comms);

          comms
            .filter((comm) => comm.platform !== MessagingPlatform.SLACK)
            .forEach((comm) => {
              // The store holds partials: an absent array means "not
              // selected", so rewriting it would wipe the other links.
              if (!hasLinkedDoBox(comm)) {
                return;
              }

              const remainingInboxEntryIds = (comm.inboxEntryIds ?? []).filter(
                (id) => id !== inboxEntryId,
              );

              modifyUserCommunication({
                ...comm,
                inboxEntryIds:
                  remainingInboxEntryIds.length > 0
                    ? remainingInboxEntryIds
                    : undefined,
              });
            });

          await handleUpdateInboxEntryCollections(inboxEntryId, {
            communicationIds: {
              remove: Array.from(messageIdsToUnlink),
            },
          });

          unlinkUserCommunicationsFromInboxEntry(
            inboxEntryId,
            Array.from(messageIdsToUnlink),
          );

          clearIntents(Array.from(messageIdsToUnlink));

          options?.onClearSelection?.();
          options?.onComplete?.();
        } catch (error) {
          log.error('Failed to unlink userCommunications:', error);
        }
      },
      [handleUpdateInboxEntryCollections],
    );

    return { handleUnlinkUserCommunications };
  };
